import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { eventUpdateRequestsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  ArrowLeft, Loader2, Inbox, CheckCircle2, XCircle, Clock, AlertCircle, RotateCcw, ExternalLink,
} from 'lucide-react';

const STATUS_META = {
  pending:    { label: 'Pending',   icon: Clock,        cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  resolved:   { label: 'Resolved',  icon: CheckCircle2, cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  dismissed:  { label: 'Dismissed', icon: XCircle,      cls: 'bg-gray-100 text-gray-700 border-gray-200' },
};

const fmt = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
};

export const AdminEventRequests = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState('pending');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resolvingId, setResolvingId] = useState(null);
  const [noteDraft, setNoteDraft] = useState({});

  const load = async (status) => {
    setLoading(true);
    try {
      const { data } = await eventUpdateRequestsAPI.adminList(status);
      setItems(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(tab); /* eslint-disable-next-line */ }, [tab]);

  const counts = useMemo(() => ({ [tab]: items.length }), [items, tab]);

  const resolve = async (id, status) => {
    setResolvingId(id);
    try {
      await eventUpdateRequestsAPI.adminResolve(id, {
        status,
        admin_note: (noteDraft[id] || '').trim() || null,
      });
      toast.success(status === 'resolved' ? 'Marked resolved' : status === 'dismissed' ? 'Dismissed' : 'Re-opened');
      await load(tab);
      setNoteDraft((d) => { const x = { ...d }; delete x[id]; return x; });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update request');
    } finally {
      setResolvingId(null);
    }
  };

  if (!user?.is_admin) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="max-w-md text-center space-y-3 p-8 bg-white border-2 border-amber-200 rounded-2xl">
          <AlertCircle className="w-10 h-10 text-amber-600 mx-auto" />
          <h1 className="text-xl font-bold text-[#1E3A5F]">Admins only</h1>
          <Link to="/dashboard">
            <Button className="btn-primary">Back to Dashboard</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-6 md:px-12" data-testid="admin-event-requests-page">
      <div className="max-w-5xl mx-auto">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 text-[#4A4A4A] hover:text-[#1E3A5F] mb-6 font-medium"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to admin
        </Link>

        <div className="flex items-center gap-3 mb-6">
          <Inbox className="w-8 h-8 text-[#1E3A5F]" />
          <div>
            <h1 className="text-3xl font-bold font-['Outfit'] text-[#1E3A5F]">Event Update Requests</h1>
            <p className="text-[#4A4A4A] text-sm">Review suggestions vendors have submitted for community events.</p>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="pending"   data-testid="tab-pending">Pending</TabsTrigger>
            <TabsTrigger value="resolved"  data-testid="tab-resolved">Resolved</TabsTrigger>
            <TabsTrigger value="dismissed" data-testid="tab-dismissed">Dismissed</TabsTrigger>
            <TabsTrigger value="all"       data-testid="tab-all">All</TabsTrigger>
          </TabsList>

          <TabsContent value={tab}>
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-6 h-6 text-[#1E3A5F] animate-spin" />
              </div>
            ) : items.length === 0 ? (
              <div className="text-center py-16 border-2 border-dashed border-gray-200 rounded-xl text-[#71717A]">
                No {tab === 'all' ? '' : tab} requests right now. 🎉
              </div>
            ) : (
              <div className="space-y-4">
                {items.map((r) => {
                  const meta = STATUS_META[r.status] || STATUS_META.pending;
                  const Icon = meta.icon;
                  return (
                    <div
                      key={r.id}
                      className="bg-white border-2 border-gray-100 rounded-xl p-5 shadow-sm"
                      data-testid={`request-${r.id}`}
                    >
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                          <Link
                            to={`/events/${r.event_id}`}
                            className="font-bold text-[#1E3A5F] hover:underline inline-flex items-center gap-1"
                            target="_blank" rel="noopener noreferrer"
                          >
                            {r.event_title || '(Event)'} <ExternalLink className="w-3.5 h-3.5" />
                          </Link>
                          <p className="text-xs text-[#71717A]">
                            {[r.event_city, r.event_state].filter(Boolean).join(', ')}
                            {r.event_date && ` · ${r.event_date}`}
                          </p>
                        </div>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${meta.cls}`}>
                          <Icon className="w-3 h-3" /> {meta.label}
                        </span>
                      </div>

                      <div className="mt-3 grid md:grid-cols-3 gap-3 text-sm">
                        <div className="md:col-span-2">
                          {r.field_hint && (
                            <p className="text-[#1E3A5F] font-semibold mb-1">
                              Field: <span className="font-normal">{r.field_hint}</span>
                            </p>
                          )}
                          <p className="text-[#4A4A4A] whitespace-pre-line">{r.message}</p>
                          {r.admin_note && (
                            <p className="mt-2 text-xs italic text-[#71717A]">
                              Admin note: {r.admin_note}
                            </p>
                          )}
                        </div>
                        <div className="text-xs text-[#71717A] md:text-right">
                          <div className="font-semibold text-[#1E3A5F]">
                            {r.requester_slug ? (
                              <Link to={`/v/${r.requester_slug}`} className="hover:underline" target="_blank" rel="noopener noreferrer">
                                {r.requester_name || r.requester_email}
                              </Link>
                            ) : (r.requester_name || r.requester_email || 'Anonymous')}
                          </div>
                          <div>{r.requester_email}</div>
                          <div className="mt-1">{fmt(r.created_at)}</div>
                          {r.resolved_at && <div>Handled {fmt(r.resolved_at)}</div>}
                        </div>
                      </div>

                      {r.status === 'pending' ? (
                        <div className="mt-4 space-y-2">
                          <Textarea
                            value={noteDraft[r.id] || ''}
                            onChange={(e) => setNoteDraft((d) => ({ ...d, [r.id]: e.target.value }))}
                            placeholder="Optional admin note (shown when viewed later)"
                            className="input-default min-h-[60px]"
                            maxLength={2000}
                            data-testid={`note-${r.id}`}
                          />
                          <div className="flex flex-wrap gap-2 justify-end">
                            <Link to={`/events/${r.event_id}/edit`} target="_blank" rel="noopener noreferrer">
                              <Button variant="outline" className="border-[#1E3A5F] text-[#1E3A5F]" size="sm">
                                Open editor
                              </Button>
                            </Link>
                            <Button
                              variant="outline"
                              className="border-gray-300 text-[#4A4A4A]"
                              size="sm"
                              onClick={() => resolve(r.id, 'dismissed')}
                              disabled={resolvingId === r.id}
                              data-testid={`dismiss-${r.id}`}
                            >
                              {resolvingId === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4 mr-1" />}
                              Dismiss
                            </Button>
                            <Button
                              className="bg-emerald-600 hover:bg-emerald-700 text-white"
                              size="sm"
                              onClick={() => resolve(r.id, 'resolved')}
                              disabled={resolvingId === r.id}
                              data-testid={`resolve-${r.id}`}
                            >
                              {resolvingId === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                              Mark resolved
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="mt-3 flex justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-[#71717A]"
                            onClick={() => resolve(r.id, 'pending')}
                            disabled={resolvingId === r.id}
                            data-testid={`reopen-${r.id}`}
                          >
                            <RotateCcw className="w-3.5 h-3.5 mr-1" />
                            Re-open
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminEventRequests;
